import Phaser from 'phaser';
import type { TilesetId } from '../config/tilesets.js';
import { TILESET_ROOTS, DEFAULT_TILESET } from '../config/tilesets.js';

/**
 * Shared layered-backdrop source — ONE definition of the cozy scene backdrop the
 * plaza (`LobbyScene`) and every in-game stage (`StageScene`) both consume, so
 * they read as the same world (S4 scene-visual parity).
 *
 * The backdrop art is a single image, `bg/BG.png` (the sunnyland region): a
 * gradient sky + soft white clouds + snow-capped mountains + far layered green
 * hills/bushes. The plaza paints it with DOM `background:url(...) repeat-x`
 * (`LobbyScene.renderPlaza`); the stages are a canvas Phaser scene, so this draws
 * the SAME image with a `tileSprite` (the Phaser way to tile a texture).
 *
 * Decorative only — no collision/sim/server touch. Both sides import the asset
 * path from here (`backdropUrl`), so a backdrop swap is one edit in one place.
 */

/** Backdrop image file under a tileset region's `bg/`. */
const BG_FILE = 'BG.png';

/** Public URL of a region's layered backdrop image (sky/clouds/mountains/hills). */
export function backdropUrl(tileset: TilesetId = DEFAULT_TILESET): string {
  return `${TILESET_ROOTS[tileset]}/bg/${BG_FILE}`;
}

/** Phaser texture key for a region's backdrop image. */
const backdropKey = (tileset: TilesetId): string => `sb-bg-${tileset}`;

/**
 * Queue the backdrop image texture for a tileset region. Call from a scene's
 * `preload()` (idempotent — Phaser skips an already-loaded key).
 */
export function preloadSceneBackdrop(
  scene: Phaser.Scene,
  tileset: TilesetId = DEFAULT_TILESET,
): void {
  if (!scene.textures.exists(backdropKey(tileset))) {
    scene.load.image(backdropKey(tileset), backdropUrl(tileset));
  }
}

export interface SceneBackdropOptions {
  /** Stage world width in px (terrain extent the camera pans across). */
  worldW: number;
  /** Camera viewport height in px (the un-zoomed canvas height). */
  worldH: number;
  /** Screen-y (px) of the ground plane (level y=0) — the backdrop's baseline. */
  groundY: number;
  /** Cosmetic tileset region (defaults to sunnyland). */
  tileset?: TilesetId;
  /**
   * Parallax factor (0..1) of the backdrop scroll vs the 1:1 world scroll — the
   * plaza uses ~0.45. The backdrop is repositioned each frame by the scene
   * (`updateParallax`) so distant mountains drift slower than the foreground.
   */
  parallax?: number;
}

/** A drawn backdrop handle: the tiled image + its parallax updater. */
export interface SceneBackdropHandle {
  image: Phaser.GameObjects.TileSprite;
  /**
   * Re-anchor the backdrop to the current camera scroll with the configured
   * parallax, so it fills the view and drifts slower than the world. Call each
   * frame after the camera moves (e.g. in the scene's render/update loop).
   */
  updateParallax(): void;
}

/**
 * Paint the shared layered backdrop (the plaza's `BG.png`: sky + clouds +
 * mountains + hills/bushes) into a Phaser scene so the stage view reads like the
 * plaza instead of an empty flat sky. The image is drawn as a wide `tileSprite`
 * (repeat-x, matching the plaza's `repeat-x`) anchored to the bottom of the
 * scene world and sized to comfortably exceed the zoomed-out camera view, with a
 * gentle parallax so the mountains drift slower than the foreground.
 *
 * Decorative only — added at a deep depth below terrain/pons; never collides.
 */
export function drawSceneBackdrop(
  scene: Phaser.Scene,
  opts: SceneBackdropOptions,
): SceneBackdropHandle {
  const tileset = opts.tileset ?? DEFAULT_TILESET;
  const parallax = opts.parallax ?? 0.45;
  const cam = scene.cameras.main;

  // The texture is 1000x750 (the plaza's full layered backdrop). The plaza shows
  // it `auto 100%` (full height, repeating across) anchored to the ground. We
  // scale it so its lower band — the hills/bushes/treeline — sits on the ground
  // plane, with the mountains + sky above, and the image tiles horizontally to
  // span every stage width plus the zoomed-out camera overscan.
  const tex = scene.textures.get(backdropKey(tileset));
  const src = tex.getSourceImage() as { width: number; height: number };
  const texW = src?.width || 1000;
  const texH = src?.height || 750;

  // Display height: span the visible above-ground band when the camera is zoomed
  // OUT (STAGE_ZOOM). The camera shows ~worldH/zoom px of height (at zoom 0.55,
  // ~782px). Anchor the backdrop's BOTTOM at the ground and stand it up the full
  // visible height so the mountains + sky fill the band above (the texture's lower
  // hill/treeline band tucks at/just below the ground, like the plaza's
  // ground-anchored backdrop — not pushed off the top of the view).
  const viewH = opts.worldH / Math.max(cam.zoom, 0.01);
  const dispH = viewH;
  const scale = dispH / texH;
  const tileW = texW * scale;

  // Span the whole world plus generous left/right overscan for the zoomed-out
  // camera + parallax travel, rounded up to whole tiles so the repeat is seamless.
  // The image is repositioned each frame, so make it comfortably wider than any
  // single view (world + 4 view-widths) — no blue gap at either world edge.
  const viewW = opts.worldW / Math.max(cam.zoom, 0.01);
  const spanW = opts.worldW + viewW * 4;
  const dispW = Math.ceil(spanW / tileW) * tileW;

  // Anchor the backdrop's BOTTOM at the ground plane (the treeline/hills sit on the
  // meadow), like the plaza's ground-anchored `bottom` background.
  const bottomY = opts.groundY;

  const image = scene.add
    .tileSprite(0, bottomY, dispW, dispH, backdropKey(tileset))
    .setOrigin(0, 1)
    .setTileScale(scale, scale)
    .setDepth(-10)
    .setScrollFactor(1);

  const updateParallax = (): void => {
    // Re-anchor horizontally so the backdrop stays under the camera while drifting
    // slower than the world (distant-mountain parallax). The tileSprite's x is the
    // slow-scrolled left edge (kept 2 view-widths left of the camera so the span
    // always covers the view); tilePositionX keeps the seamless texture phase.
    const camLeft = cam.scrollX;
    const slow = camLeft * (1 - parallax);
    image.x = slow - viewW * 2;
    image.tilePositionX = (camLeft * parallax) / scale;
  };
  updateParallax();

  return { image, updateParallax };
}
